import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { SortEvent, TableSortableDirective } from './table-sortable.directive';

/**
 * Directive that displays a table with sorting, row selection, and keyboard navigation support.
 *
 * @remarks
 * This directive can be applied to `<table>` elements and works with Angular CDK Table. It manages:
 * - **Row Selection**: Track and manage selected rows via {@link selectRow} output
 * - **Row Interactions**: Emit events on row click through {@link rowClick} output
 * - **Column Sorting**: Integrate with {@link TableSortableDirective} for header sorting
 * - **Focus Management**: Coordinate with {@link TableFocusDirective} for keyboard navigation
 * - **Header Registration**: Automatically manages sortable column headers
 *
 * It works best when combined with `[bbTableFocus]` for
 * keyboard navigation and `[bbRow]` for row interactivity.
 *
 * #### Sorting Setup
 *
 * To enable sorting on columns, apply {@link TableSortableDirective} to `<th>` headers and handle
 * the `(sort)` output event. Each column header requires a `<span class="th-content">` element
 * to display the sort indicator arrows.
 *
 * #### Row Selection Setup
 *
 * To enable row selection and keyboard navigation, add `[bbTableFocus]` to the table and
 * `[bbRow]="rowData"` to each row. This enables:
 * - Mouse click to select rows
 * - Keyboard arrow keys to navigate between rows
 * - Visual focus indicators on the currently focused row
 *
 * #### Angular CDK Table Integration
 *
 * The `CdkTable` provides flexible templating, dynamic column management, and accessible DOM structure.
 * It integrates with `bbTable` to combine CDK's features with sorting and selection capabilities.
 *
 * Key advantages of using Angular CDK tables:
 * - Dynamic column management: Add, remove, or replace columns through configuration
 * - Flexible templating system for custom cell rendering
 * - Built-in accessibility features with role and aria attributes
 * - Seamless integration with Angular's change detection
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   selector: 'bb-cdk-table-example-ui',
 *   imports: [
 *     TableModule,
 *     CdkTableModule,
 *     NgComponentOutlet,
 *     AsyncPipe,
 *     DecimalPipe,
 *   ],
 *   template: `
 *     <table
 *       cdk-table
 *       [bbTable]="dataSource | async"
 *       class="table table-hover"
 *       [dataSource]="dataSource"
 *     >
 *       <ng-container cdkColumnDef="name">
 *         <th bbSortable="name" cdk-header-cell *cdkHeaderCellDef>
 *           <span class="th-content">Country</span>
 *         </th>
 *         <td cdk-cell *cdkCellDef="let element">
 *           {{ element.name }}
 *         </td>
 *       </ng-container>
 *
 *       <ng-container cdkColumnDef="area">
 *         <th cdk-header-cell *cdkHeaderCellDef>
 *           <span class="th-content">Area</span>
 *         </th>
 *         <td cdk-cell *cdkCellDef="let element">
 *           {{ element.area | number }}
 *         </td>
 *       </ng-container>
 *
 *       <ng-container cdkColumnDef="population">
 *         <th cdk-header-cell *cdkHeaderCellDef>
 *           <span class="th-content">Population</span>
 *         </th>
 *         <td cdk-cell *cdkCellDef="let element">
 *           {{ element.population | number }}
 *         </td>
 *       </ng-container>
 *
 *       <ng-container cdkColumnDef="flag">
 *         <th cdk-header-cell *cdkHeaderCellDef>
 *           <span class="th-content">Flag</span>
 *         </th>
 *         <td cdk-cell *cdkCellDef="let element">
 *           <img [alt]="'Flag of ' + element.name" [src]="pathFlagAssets + element.flag" width="32" />
 *         </td>
 *       </ng-container>
 *
 *       <tr cdk-header-row *cdkHeaderRowDef="displayColumns"></tr>
 *       <tr cdk-row *cdkRowDef="let row; columns: displayColumns;"></tr>
 *     </table>
 *   `,
 *   standalone: true,
 * })
 * export class CdkTableExampleComponent {
 *   readonly displayColumns: string[] = ['name', 'area', 'population', 'flag'];
 *   readonly dataSource = new BehaviorSubject<CountryData[]>(countryData);
 *   readonly pathFlagAssets = 'https://upload.wikimedia.org/wikipedia/commons/';
 * }
 * ```
 *
 */
@Directive({
  selector: 'table[bbTable]',
  standalone: false,
})
export class TableDirective {
  /**
   * The data source for the table.
   *
   * @remarks
   * Accepts an array of data objects that will be rendered as table rows. This input receives
   * the data passed via the `[bbTable]` property binding. The directive does not manage data
   * sorting or filtering - it only provides the infrastructure for row selection and focus management.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="countries">
   *   <tbody>
   *     @for (country of countries; track country.id) {
   *       <tr [bbRow]="country">
   *         <td>{{ country.name }}</td>
   *         <td>{{ country.area | number }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('bbTable') dataSource?: Array<Object>;
  /**
   * Emits an event when a row is selected.
   *
   * @remarks
   * Fires whenever the selection state changes, either through user interaction (clicking rows,
   * clicking the "select all" checkbox) or programmatic calls to {@link toggleItem} and {@link toggleAllItems}.
   * The emitted value is a `Set<Object>` containing all currently selected row objects.
   *
   * This output should be bound in the template to handle row selection changes. The parent
   * component is responsible for storing the selected state and reacting to selection changes.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="countries" (selectRow)="onRowsSelected($event)" bbTableFocus>
   *   <tbody>
   *     @for (country of countries; track country.id) {
   *       <tr [bbRow]="country">
   *         <td>{{ country.name }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  @Output() selectRow = new EventEmitter();
  /**
   * Emits an event when a table row is clicked.
   *
   * @remarks
   * Fired when the user clicks on a row (specifically when the `bbRow` directive detects a click).
   * The emitted value is the data object associated with the clicked row.
   *
   * This event enables click-based row interactions like navigation, detail views, or row expansion.
   * To enable this functionality, rows must have the `[bbRow]="rowData"` directive applied.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="countries" (rowClick)="onRowClicked($event)" bbTableFocus>
   *   <tbody>
   *     @for (country of countries; track country.id) {
   *       <tr [bbRow]="country">
   *         <td>{{ country.name }}</td>
   *         <td>{{ country.population | number }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  @Output() rowClick = new EventEmitter();
  /**
   * Emits an event when a column sort is triggered.
   *
   * @remarks
   * Fired when the user clicks on a sortable column header to change the sort direction.
   * The emitted value is a {@link SortEvent} containing:
   * - `column`: The name of the column being sorted (matches the `bbSortable` directive value)
   * - `direction`: The sort direction ('asc' for ascending, 'desc' for descending, or `null` for unsorted)
   *
   * The parent component must listen to this event and apply the sort to the data source.
   * To enable sorting, apply the `[bbSortable]="'columnName'"` directive to `<th>` headers
   * and ensure each header contains a `<span class="th-content">` element.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="countries" (sort)="onSort($event)">
   *   <thead>
   *     <tr>
   *       <th bbSortable="name" scope="col">
   *         <span class="th-content">Country</span>
   *       </th>
   *       <th bbSortable="area" scope="col">
   *         <span class="th-content">Area</span>
   *       </th>
   *       <th bbSortable="population" scope="col">
   *         <span class="th-content">Population</span>
   *       </th>
   *     </tr>
   *   </thead>
   *   <tbody>
   *     @for (country of countries; track country.id) {
   *       <tr [bbRow]="country">
   *         <td>{{ country.name }}</td>
   *         <td>{{ country.area | number }}</td>
   *         <td>{{ country.population | number }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  @Output() sort = new EventEmitter();

  /**
   * Tracks the currently selected row objects.
   */
  selectedRows: Set<Object> = new Set();

  /**
   * Manages all registered sortable column headers.
   */
  headers: Set<TableSortableDirective> = new Set();

  /**
   * Registers a sortable column header with this table.
   *
   * @remarks
   * Called automatically by {@link TableSortableDirective} when it initializes. This internal
   * method adds the header to the {@link headers} set so the table can coordinate sort state
   * across all column headers.
   *
   * @param header - The {@link TableSortableDirective} instance to register
   */
  registerHeader(header: TableSortableDirective) {
    this.headers.add(header);
  }

  /**
   * Unregisters a sortable column header from this table.
   *
   * @remarks
   * Called automatically by {@link TableSortableDirective} when it is destroyed. This internal
   * method removes the header from the {@link headers} set to prevent memory leaks.
   *
   * @param header - The {@link TableSortableDirective} instance to unregister
   */
  unRegisterHeader(header: TableSortableDirective) {
    this.headers.delete(header);
  }

  /**
   * Handles row click events.
   *
   * @remarks
   * Called internally by {@link TableRowDirective} when a row is clicked. This method emits
   * the row data through the {@link rowClick} output so parent components can respond to row selection.
   *
   * @param rowItem - The data object associated with the clicked row
   */
  onRowClick(rowItem: any) {
    this.rowClick.emit(rowItem);
  }

  /**
   * Checks if a given row is currently selected.
   *
   * @remarks
   * Used by {@link TableRowDirective} to apply the 'selected' CSS class to rows that match
   * the current selection state. Returns true if the row object is in the {@link selectedRows} set.
   *
   * @param row - The data object to check for selection
   * @returns `true` if the row is selected, `false` otherwise
   */
  isSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  /**
   * Toggles the selection state of a single row.
   *
   * @remarks
   * Adds or removes a row from the {@link selectedRows} set based on the `isChecked` parameter.
   * After updating the selection, emits the updated selection set through the {@link selectRow} output.
   * This method is typically called when a user clicks a row checkbox or clicks a row directly.
   *
   * @param row - The data object to toggle
   * @param isChecked - If true, adds the row to the selection; if false, removes it
   */
  toggleItem(row: any, isChecked: boolean) {
    if (isChecked) {
      this.selectedRows.add(row);
    } else {
      this.selectedRows.delete(row);
    }
    this.selectRow.emit(this.selectedRows);
  }

  /**
   * Toggles selection of all rows in the table.
   *
   * @remarks
   * If all rows are currently selected, deselects them all. If any rows are unselected,
   * selects all rows in the {@link dataSource}. After updating the selection, emits
   * the updated selection set through the {@link selectRow} output.
   * This method is typically called by a "select all" checkbox in the table header.
   */
  toggleAllItems() {
    if (this.isSelectedAll()) {
      this.selectedRows = new Set();
    } else {
      this.selectedRows = new Set(this.dataSource);
    }
    this.selectRow.emit(this.selectedRows);
  }

  /**
   * Checks if all rows are currently selected.
   *
   * @remarks
   * Returns true only when the number of selected rows equals the total number of rows in
   * the {@link dataSource}. Used by {@link toggleAllItems} to determine the toggle direction
   * and by the "select all" checkbox to reflect the current selection state.
   *
   * @returns `true` if all rows are selected, `false` if any row is unselected or dataSource is empty
   */
  isSelectedAll(): boolean {
    if (this.dataSource) {
      const matches = this.dataSource.filter((item) => this.selectedRows.has(item));

      return matches.length === this.dataSource.length;
    }

    return false;
  }

  /**
   * Checks if some (but not all) rows are currently selected.
   *
   * @remarks
   * Returns true when at least one row is selected but not all rows are selected.
   * Used by the "select all" checkbox to display an indeterminate state, indicating
   * that only a partial selection is active.
   *
   * @returns `true` if at least one row is selected and at least one row is unselected, `false` otherwise
   */
  isSelectedPart(): boolean {
    if (this.dataSource) {
      return this.selectedRows.size > 0 && this.selectedRows.size < this.dataSource.length;
    }

    return false;
  }

  /**
   * Handles column sort events from sortable headers.
   *
   * @remarks
   * Called automatically by {@link TableSortableDirective} when a user clicks a sortable column header.
   * This method:
   * 1. Resets all other column headers to unsorted state (clears their direction and menu options)
   * 2. Emits the sort event through the {@link sort} output for the parent component to apply sorting
   *
   * This ensures only one column can be actively sorted at a time.
   *
   * @param sortEvent - The {@link SortEvent} containing the column name and sort direction
   */
  onSort(sortEvent: SortEvent) {
    this.headers.forEach((header) => {
      if (header.bbSortable !== sortEvent.column) {
        header.direction = null;
        header.markAllMenuOptionAsInactive();
      }
    });
    this.sort.emit(sortEvent);
  }
}
